import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import {
  ArrowLeft, MapPin, Clock, Phone, Globe, Navigation,
  Grape, Wine, Star, Calendar, DollarSign,
  Dog, UtensilsCrossed, Compass, Mountain, Music,
  Bed, ShoppingBag, Store, Sun, Trees, Sandwich,
  Wifi, Accessibility, Palette, Leaf, PawPrint,
  Zap, Users, Heart, Camera, Tag, GlassWater, Bus, DoorOpen
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';
import { getWineryRating } from '@/lib/api';
import SafetyBanner from '@/components/SafetyBanner';

// Features that every winery has — not worth showing
const SKIP_FEATURES = new Set([
  'Wine Tastings',
  'Inside Facilities',
  'Customer Washrooms',
  'Outside Facilities',
  'Kid-Friendly',
  'Wine & Food Tastings',
  'Wines in Wines of BC Stores',
]);

// Small accessibility/convenience icons shown separately
const ACCESSIBILITY_FEATURES = new Set([
]);

// Icon + color mapping for highlight features
const FEATURE_CONFIG = {
  // Legacy names (kept for backward compatibility)
  'Restaurant / Bistro':   { icon: UtensilsCrossed, color: 'text-orange-600 bg-orange-50' },
  'Picnic Area':           { icon: Trees, color: 'text-green-700 bg-green-50' },
  'Picnic Food Available': { icon: Sandwich, color: 'text-amber-700 bg-amber-50' },
  'Bring Your Own Food':   { icon: ShoppingBag, color: 'text-amber-600 bg-amber-50' },
  'Scenic Point':          { icon: Mountain, color: 'text-emerald-600 bg-emerald-50' },
  'Vineyard Views':        { icon: Mountain, color: 'text-emerald-700 bg-emerald-50' },
  'Accommodation':         { icon: Bed, color: 'text-indigo-600 bg-indigo-50' },
  'Event Venue':           { icon: Calendar, color: 'text-blue-600 bg-blue-50' },
  'Outdoor Seating':       { icon: Sun, color: 'text-yellow-600 bg-yellow-50' },
  'Activities for Kids':   { icon: Star, color: 'text-sky-600 bg-sky-50' },
  'Local food pairings':   { icon: UtensilsCrossed, color: 'text-orange-600 bg-orange-50' },
  'Solar-powered winery':  { icon: Sun, color: 'text-yellow-600 bg-yellow-50' },
  'regenerative agriculture': { icon: Leaf, color: 'text-green-600 bg-green-50' },
  // WineBC amenity names
  'Restaurant':                     { icon: UtensilsCrossed, color: 'text-orange-600 bg-orange-50' },
  'Wine Shop':                      { icon: Store, color: 'text-purple-700 bg-purple-50' },
  'Wine Club':                      { icon: Wine, color: 'text-[#722F37] bg-red-50' },
  'Patio':                          { icon: Sun, color: 'text-yellow-600 bg-yellow-50' },
  'Tours':                          { icon: Compass, color: 'text-sky-700 bg-sky-50' },
  'Live Music':                     { icon: Music, color: 'text-pink-600 bg-pink-50' },
  'Gallery':                        { icon: Palette, color: 'text-fuchsia-600 bg-fuchsia-50' },
  'Scenic Views':                   { icon: Mountain, color: 'text-emerald-600 bg-emerald-50' },
  'Accommodations':                 { icon: Bed, color: 'text-indigo-600 bg-indigo-50' },
  'Special Events':                 { icon: Calendar, color: 'text-blue-600 bg-blue-50' },
  'Weddings & Private Events':      { icon: Heart, color: 'text-rose-600 bg-rose-50' },
  'Large Group Bookings Available': { icon: Users, color: 'text-blue-700 bg-blue-50' },
  'EV Charging Station':            { icon: Zap, color: 'text-lime-600 bg-lime-50' },
  'Instagram Worthy Setting':       { icon: Camera, color: 'text-purple-600 bg-purple-50' },
  'Seated Tastings':                { icon: Wine, color: 'text-[#722F37] bg-red-50' },
  'Tasting Experiences $5 - $15':   { icon: DollarSign, color: 'text-green-700 bg-green-50' },
  'Tour Bus Parking':               { icon: Bus, color: 'text-gray-600 bg-gray-50' },
  'Promotions for Locals':          { icon: Tag, color: 'text-orange-700 bg-orange-50' },
  'Wine for Sale by the Glass':     { icon: GlassWater, color: 'text-blue-600 bg-blue-50' },
  'Walk-ins Welcome':               { icon: DoorOpen, color: 'text-green-600 bg-green-50' },
  'Golf Course':                    { icon: Compass, color: 'text-green-700 bg-green-50' },
  'Resort':                         { icon: Bed, color: 'text-indigo-600 bg-indigo-50' },
  'Indigenous Museum':              { icon: Palette, color: 'text-amber-700 bg-amber-50' },
  'Wheelchair Accessible':          { icon: Accessibility, color: 'text-blue-600 bg-blue-50', label: 'Accessible' },
  'Pets Allowed':                   { icon: PawPrint, color: 'text-amber-600 bg-amber-50', label: 'Pets OK' },
  'Pet Friendly':                   { icon: PawPrint, color: 'text-amber-600 bg-amber-50', label: 'Pets OK' },
  'Family Friendly':                { icon: Users, color: 'text-sky-600 bg-sky-50', label: 'Family' },
  'Gift Shop':                      { icon: ShoppingBag, color: 'text-purple-600 bg-purple-50' },
  'Hidden Gem':                     { icon: Star, color: 'text-yellow-600 bg-yellow-50' },
  'Free WiFi':                      { icon: Wifi, color: 'text-blue-500 bg-blue-50' },
};

// Accessibility icon mapping
const ACCESSIBILITY_CONFIG = {
  'Wheelchair Accessible': { icon: Accessibility, label: 'Accessible' },
  'Pets Allowed':          { icon: PawPrint, label: 'Pets OK' },
  'Pet Friendly':          { icon: PawPrint, label: 'Pets OK' },
  'Free WiFi':             { icon: Wifi, label: 'WiFi' },
  'Family Friendly':       { icon: Users, label: 'Family' },
};

const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);

function isSafeUrl(url) {
  try {
    const u = new URL(url);
    return u.protocol === 'https:' || u.protocol === 'http:';
  } catch { return false; }
}

export default function WineryDetail({ winery, onClose, safetyAlerts, safetySeverity }) {
  const [rating, setRating] = useState({ average: null, count: 0 });
  const [browserUrl, setBrowserUrl] = useState(null);
  const [dialPhone, setDialPhone] = useState(null);

  // Fetch rating when winery changes
  useEffect(() => {
    if (winery?.id) {
      getWineryRating(winery.id).then(setRating);
    }
  }, [winery?.id]);

  if (!winery) return null;

  const mapsUrl = `https://www.google.com/maps/dir/?api=1&destination=${winery.latitude},${winery.longitude}`;

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm"
      onClick={onClose}
    >
      <motion.div
        initial={{ y: '100%' }}
        animate={{ y: 0 }}
        exit={{ y: '100%' }}
        transition={{ type: 'spring', damping: 25, stiffness: 300 }}
        onClick={(e) => e.stopPropagation()}
        className="absolute bottom-0 left-0 right-0 max-h-[90vh] bg-white rounded-t-3xl overflow-hidden"
      >
        {/* Header with Back Button */}
        <div className="bg-white z-10 px-4 py-3 border-b border-stone-100 flex items-center gap-3">
          <button
            onClick={onClose}
            className="flex items-center gap-2 px-3 py-2 rounded-lg bg-stone-100 hover:bg-stone-200 transition-colors text-stone-700 font-medium"
          >
            <ArrowLeft className="w-5 h-5" />
            Back
          </button>
          <span className="text-stone-400 text-sm">Back to route</span>
        </div>

        {/* Scrollable Content */}
        <div className="overflow-y-auto max-h-[calc(90vh-40px)] pb-12">
          {/* Safety alert for this winery */}
          {safetySeverity && safetySeverity !== 'none' && (
            <div className="px-4 pt-3">
              <SafetyBanner key={winery?.name} alerts={safetyAlerts} severity={safetySeverity} />
            </div>
          )}
          {/* Hero — Winery Name */}
          <div className="h-48 bg-gradient-to-br from-[#722F37] to-[#5a252c] flex flex-col items-center justify-center px-6 text-center">
            <Wine className="w-7 h-7 text-[#C9A227] mb-3 opacity-80" />
            <h2
              className="text-3xl text-white leading-snug italic"
              style={{ fontFamily: "'Playfair Display', Georgia, serif" }}
            >
              {winery.name}
            </h2>
            <div className="flex items-center gap-1.5 text-white/60 text-sm mt-3">
              <MapPin className="w-3.5 h-3.5" />
              <span>{winery.region}</span>
            </div>
          </div>

          {/* Content */}
          <div className="p-5 space-y-6">
            {/* Quick Actions */}
            <div className="flex gap-3">
              <Button
                onClick={() => isIOS ? (window.location.href = mapsUrl) : window.open(mapsUrl, '_blank')}
                className="flex-1 h-14 bg-[#722F37] hover:bg-[#5a252c] text-white rounded-xl"
              >
                <Navigation className="w-5 h-5 mr-2" />
                Navigate
              </Button>
              {winery.phone && (
                <Button
                  variant="outline"
                  onClick={() => isIOS
                    ? window.open(`tel:${winery.phone}`, '_self')
                    : setDialPhone(winery.phone)
                  }
                  className="h-14 px-5 rounded-xl"
                >
                  <Phone className="w-5 h-5" />
                </Button>
              )}
              {winery.website && isSafeUrl(winery.website) && (
                <Button
                  variant="outline"
                  onClick={() => setBrowserUrl(winery.website)}
                  className="h-14 px-5 rounded-xl"
                >
                  <Globe className="w-5 h-5" />
                </Button>
              )}
            </div>

            {/* Info Grid */}
            <div className="grid grid-cols-2 gap-3">
              {winery.hours_text && (
                <InfoCard icon={Clock} label="Hours" value={winery.hours_text} />
              )}
              {winery.restaurant_open && (
                <InfoCard icon={UtensilsCrossed} label="Dining" value="Open Now" className="bg-amber-50 border border-amber-200" />
              )}
              <InfoCard
                icon={DollarSign}
                label="Tasting Fee"
                value={formatTastingFee(winery.tasting_fee, winery.tasting_fee_updated_at)}
              />
              {winery.reservation_required !== undefined && winery.reservation_required !== null && (
                <InfoCard
                  icon={Calendar}
                  label="Reservations"
                  value={winery.reservation_required
                    ? (winery.phone ? `Required · Call ${winery.phone}` : 'Required')
                    : 'Walk-in OK'}
                />
              )}
              {winery.address && (
                <InfoCard icon={MapPin} label="Address" value={winery.address} className="col-span-2" />
              )}
            </div>

            {/* Description - only show revised (original) description, never raw scraped text */}
            {winery.revised_description && (
              <div>
                <h3 className="font-semibold text-stone-800 mb-2">About</h3>
                <p className="text-stone-600 text-sm leading-relaxed">
                  {winery.revised_description}
                </p>
              </div>
            )}

            {/* Grapes & Wines */}
            {(winery.grapes_grown?.length > 0 || winery.wines_offered?.length > 0) && (
              <div className="grid grid-cols-2 gap-4">
                {winery.grapes_grown?.length > 0 && (
                  <div>
                    <h4 className="text-sm font-medium text-stone-700 mb-2 flex items-center gap-1.5">
                      <Grape className="w-4 h-4 text-purple-600" />
                      Grapes Grown
                    </h4>
                    <div className="flex flex-wrap gap-1.5">
                      {winery.grapes_grown.map(grape => (
                        <span key={grape} className="text-xs px-2 py-1 rounded-full bg-stone-100 text-stone-600">
                          {grape}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
                {winery.wines_offered?.length > 0 && (
                  <div>
                    <h4 className="text-sm font-medium text-stone-700 mb-2 flex items-center gap-1.5">
                      <Wine className="w-4 h-4 text-[#722F37]" />
                      Wines Offered
                    </h4>
                    <div className="flex flex-wrap gap-1.5">
                      {winery.wines_offered.map(wine => (
                        <span key={wine} className="text-xs px-2 py-1 rounded-full bg-stone-100 text-stone-600">
                          {wine}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Features - Highlights */}
            {winery.features?.length > 0 && (() => {
              const highlights = winery.features.filter(f => !SKIP_FEATURES.has(f) && !ACCESSIBILITY_FEATURES.has(f));
              const accessibility = winery.features.filter(f => ACCESSIBILITY_FEATURES.has(f));
              if (highlights.length === 0 && accessibility.length === 0) return null;
              return (
                <div className="space-y-3">
                  {highlights.length > 0 && (
                    <div>
                      <h3 className="font-semibold text-stone-800 mb-3">Amenities</h3>
                      <div className="flex flex-wrap gap-2">
                        {highlights.map(feat => {
                          const config = FEATURE_CONFIG[feat];
                          const Icon = config?.icon || Star;
                          const colorClass = config?.color || 'text-stone-600 bg-stone-100';
                          return (
                            <span
                              key={feat}
                              className={`flex items-center gap-1.5 text-sm px-3 py-1.5 rounded-full font-medium ${colorClass}`}
                            >
                              <Icon className="w-4 h-4" />
                              {feat}
                            </span>
                          );
                        })}
                      </div>
                    </div>
                  )}
                  {accessibility.length > 0 && (
                    <div className="flex items-center gap-3 pt-1">
                      {accessibility.map(feat => {
                        const config = ACCESSIBILITY_CONFIG[feat];
                        if (!config) return null;
                        const Icon = config.icon;
                        return (
                          <span key={feat} className="flex items-center gap-1 text-xs text-stone-500">
                            <Icon className="w-3.5 h-3.5" />
                            {config.label}
                          </span>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            })()}

            {/* Food Service */}
            {winery.food_service && (() => {
              const serviceConfig = {
                'Full Service':        { icon: UtensilsCrossed, color: 'text-orange-700 bg-orange-50', label: 'Full Service Restaurant' },
                'Tapas & Charcuterie': { icon: Sandwich,        color: 'text-amber-700 bg-amber-50',  label: 'Tapas & Charcuterie' },
                'Food Truck':          { icon: ShoppingBag,     color: 'text-yellow-700 bg-yellow-50', label: 'Food Truck' },
                'Pizza':               { icon: UtensilsCrossed, color: 'text-red-700 bg-red-50',       label: 'Pizza' },
                'Bakery':              { icon: Star,            color: 'text-stone-700 bg-stone-100',  label: 'Bakery' },
                'Sandwiches':          { icon: Sandwich,        color: 'text-green-700 bg-green-50',   label: 'Sandwiches' },
                'Charcuterie Board':   { icon: Sandwich,        color: 'text-amber-700 bg-amber-50',   label: 'Food options available: Charcuterie, Cheese, Meats & Snacks' },
                'Seasonal Kitchen':    { icon: UtensilsCrossed, color: 'text-orange-700 bg-orange-50',  label: 'Kitchen Open Fri–Sun (Seasonal)', detail: 'May–Jun: Fri–Sat 3–6pm · Sun 9am–6pm (waffle breakfast 9–11:30am)\nJul–Aug: Fri–Sat Noon–6pm · Sun 9am–6pm (waffle breakfast 9–11:30am)' },
              };
              const config = serviceConfig[winery.food_service];
              if (!config) return null;
              const Icon = config.icon;
              return (
                <div>
                  <h3 className="font-semibold text-stone-800 mb-3">Dining</h3>
                  <span className={`flex items-center gap-1.5 text-sm px-3 py-1.5 rounded-full font-medium w-fit ${config.color}`}>
                    <Icon className="w-4 h-4" />
                    {config.label}
                  </span>
                  {config.detail && (
                    <p className="text-sm text-stone-500 mt-2 leading-relaxed whitespace-pre-line">{config.detail}</p>
                  )}
                </div>
              );
            })()}

            {/* Vineyard Experiences */}
            {winery.vineyard_experiences?.length > 0 && (() => {
              const badges = winery.vineyard_experiences.filter(e => !e.includes('|'));
              const cards  = winery.vineyard_experiences.filter(e => e.includes('|'));
              return (
                <div className="space-y-3">
                  {badges.length > 0 && (
                    <div>
                      <h3 className="font-semibold text-stone-800 mb-3">Unique Experiences</h3>
                      <div className="flex flex-wrap gap-2">
                        {badges.map(exp => (
                          <Badge
                            key={exp}
                            className="bg-purple-100 text-purple-800 hover:bg-purple-100 px-3 py-1.5"
                          >
                            <Star className="w-3 h-3 mr-1.5" />
                            {formatFeature(exp)}
                          </Badge>
                        ))}
                      </div>
                    </div>
                  )}
                  {cards.length > 0 && (
                    <div>
                      <h3 className="font-semibold text-stone-800 mb-3">Bespoke Packages</h3>
                      <div className="space-y-2">
                        {cards.map(exp => {
                          const [title, price] = exp.split('|');
                          return (
                            <div key={exp} className="flex items-center justify-between bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
                              <span className="flex items-center gap-1.5 text-sm font-medium text-amber-900">
                                <Star className="w-3.5 h-3.5 text-amber-600" />
                                {title}
                              </span>
                              <span className="text-xs text-amber-700 font-medium">{price}</span>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </div>
              );
            })()}

            {/* User Rating */}
            {rating.count > 0 && (
              <div className="pt-4 border-t border-stone-100">
                <div className="flex items-center justify-center gap-2">
                  <div className="flex items-center gap-1">
                    {[1, 2, 3, 4, 5].map((star) => (
                      <Star
                        key={star}
                        className={cn(
                          "w-4 h-4",
                          star <= Math.round(rating.average)
                            ? "fill-amber-400 text-amber-400"
                            : "text-stone-300"
                        )}
                      />
                    ))}
                  </div>
                  <span className="text-sm text-stone-600">
                    {rating.average.toFixed(1)}
                  </span>
                  <span className="text-xs text-stone-400">
                    ({rating.count} {rating.count === 1 ? 'rating' : 'ratings'})
                  </span>
                </div>
              </div>
            )}

            {/* Last Verified */}
            {winery.last_verified && (
              <p className="text-xs text-stone-400 text-center pt-4">
                Last verified: {new Date(winery.last_verified).toLocaleDateString()}
              </p>
            )}
          </div>
        </div>
      </motion.div>

      {/* In-app browser */}
      {browserUrl && (
        <WineryBrowser
          url={browserUrl}
          name={winery.name}
          onClose={() => setBrowserUrl(null)}
        />
      )}

      {/* Phone confirmation (Android only) */}
      {dialPhone && (
        <PhoneDialer
          phone={dialPhone}
          name={winery.name}
          onClose={() => setDialPhone(null)}
        />
      )}
    </motion.div>
  );
}

function WineryBrowser({ url, name, onClose }) {
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="absolute inset-0 z-20 bg-black/60 flex items-end"
      onClick={onClose}
    >
      <motion.div
        initial={{ y: 40, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        exit={{ y: 40, opacity: 0 }}
        transition={{ type: 'spring', damping: 25, stiffness: 300 }}
        onClick={(e) => e.stopPropagation()}
        className="w-full bg-white rounded-t-3xl p-6 space-y-5"
      >
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-[#722F37]/10 flex items-center justify-center flex-shrink-0">
            <Globe className="w-5 h-5 text-[#722F37]" />
          </div>
          <div className="min-w-0">
            <p className="font-semibold text-stone-800 truncate">{name}</p>
            <p className="text-xs text-stone-400 truncate">{url.replace(/^https?:\/\//, '')}</p>
          </div>
        </div>

        <p className="text-sm text-stone-500">
          This will open in your browser. Use your browser's back button or swipe to return to the wine guide.
        </p>

        <button
          onClick={() => { if (isSafeUrl(url)) window.location.href = url; }}
          className="flex items-center justify-center gap-2 w-full py-3.5 bg-[#722F37] text-white font-semibold rounded-xl"
        >
          <Globe className="w-4 h-4" />
          Visit Website
        </button>

        <button
          onClick={onClose}
          className="w-full py-3 text-stone-500 text-sm font-medium"
        >
          Cancel
        </button>
      </motion.div>
    </motion.div>
  );
}

function PhoneDialer({ phone, name, onClose }) {
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="absolute inset-0 z-20 bg-black/60 flex items-end"
      onClick={onClose}
    >
      <motion.div
        initial={{ y: 40, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        exit={{ y: 40, opacity: 0 }}
        transition={{ type: 'spring', damping: 25, stiffness: 300 }}
        onClick={(e) => e.stopPropagation()}
        className="w-full bg-white rounded-t-3xl p-6 space-y-5"
      >
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-green-50 flex items-center justify-center flex-shrink-0">
            <Phone className="w-5 h-5 text-green-600" />
          </div>
          <div className="min-w-0">
            <p className="font-semibold text-stone-800 truncate">{name}</p>
            <p className="text-lg font-mono text-stone-600">{phone}</p>
          </div>
        </div>

        <button
          onClick={() => { window.location.href = `tel:${phone}`; }}
          className="flex items-center justify-center gap-2 w-full py-3.5 bg-green-600 text-white font-semibold rounded-xl"
        >
          <Phone className="w-4 h-4" />
          Call
        </button>

        <button
          onClick={onClose}
          className="w-full py-3 text-stone-500 text-sm font-medium"
        >
          Cancel
        </button>
      </motion.div>
    </motion.div>
  );
}

function InfoCard({ icon: Icon, label, value, className }) {
  return (
    <div className={cn("p-3 rounded-xl bg-stone-50", className)}>
      <div className="flex items-center gap-2 text-stone-500 text-xs mb-1">
        <Icon className="w-3.5 h-3.5" />
        {label}
      </div>
      <p className="text-stone-800 text-sm font-medium">{value}</p>
    </div>
  );
}

function formatFeature(str) {
  return str.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
}

function formatTastingFee(fee, updatedAt) {
  if (!fee) return 'Call ahead for pricing';
  return fee;
}
